import { isString } from "lodash"
import { monotonicFactory } from "ulid"
import { v4 as uuidV4 } from "uuid"

import { Job, JobFilter, JobKey, JobRun, JobRunState, JobState, Match, SortDirection } from "../models/lookoutModels"

export function randomInt(min: number, max: number, rand: () => number) {
  const range = max - min
  return min + Math.floor(rand() * range)
}

export function mulberry32(a: number): () => number {
  return () => {
    let t = (a += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function seededUuid(rand: () => number): () => string {
  return () =>
    uuidV4({
      rng: () => {
        const floatArray = new Float32Array(4)
        floatArray[0] = rand()
        floatArray[1] = rand()
        floatArray[2] = rand()
        floatArray[3] = rand()
        return new Uint8Array(floatArray.buffer)
      },
    })
}

const MS_PER_SECOND = 1000
const MS_PER_MINUTE = 60 * MS_PER_SECOND
const MS_PER_DAY = 24 * 60 * MS_PER_MINUTE

// Base time that all fake dates (ULID timestamps, job submitted/transition times, run times) are derived from: a week
// before the module is loaded, so generated dates are recent but never in the future
const BASE_TIME_MS = Date.now() - 7 * MS_PER_DAY

// Generates 26 character lower-case ULIDs, as used for real job and job run IDs (see util.NewULID). They are
// lower-cased because Kubernetes requires names to be valid DNS subdomain names.
export function seededUlid(rand: () => number): () => string {
  const generateUlid = monotonicFactory(rand)
  let counter = 0
  return () => generateUlid(BASE_TIME_MS + counter++ * MS_PER_SECOND).toLowerCase()
}

export async function simulateApiWait(abortSignal?: AbortSignal): Promise<void> {
  await new Promise((resolve, reject) => {
    const timeoutId = setTimeout(resolve, randomInt(50, 250, Math.random))
    abortSignal?.addEventListener("abort", () => {
      clearTimeout(timeoutId)
      reject()
    })
  })
}

export function makeRandomJobs(nJobs: number, seed: number, nQueues = 10, nJobSets = 100, state?: JobState): Job[] {
  const rand = mulberry32(seed)
  const uuid = seededUuid(rand)
  const ulid = seededUlid(rand)
  const annotationKeys = ["hyperparameter", "some/very/long/annotation/key/name/with/forward/slashes", "region"]
  const numAnnotationValues = 10
  const annotationValues = [...Array(numAnnotationValues)].map(() => uuid())

  const queues = Array.from(Array(nQueues).keys()).map((i) => `queue-${i + 1}`)
  const jobSets = Array.from(Array(nJobSets).keys()).map((i) => `job-set-${i + 1}`)

  const jobs: Job[] = []
  for (let i = 0; i < nJobs; i++) {
    const jobId = ulid()
    // Every fourth job has 3-5 runs so that multi-run behaviour is easy to exercise
    const nRuns = randomInt(0, 3, rand)
    const { runs, lastRunRuntimeSeconds } = createJobRuns(i % 4 === 3 ? 3 + (i % 3) : nRuns, jobId, rand, uuid, ulid)
    // As in the real API, these job-level fields come from the job's latest run
    const lastRun = runs.at(-1)

    jobs.push({
      gpu: randomInt(0, 8, rand),
      lastActiveRunId: runs.length > 0 ? runs[runs.length - 1].runId : undefined,
      owner: uuid(),
      namespace: uuid(),
      priority: randomInt(0, 1000, rand),
      runs: runs,
      submitted: randomDate(BASE_TIME_MS, BASE_TIME_MS + 3 * MS_PER_DAY, rand),
      cpu: randomElement(CPU_SIZES, rand),
      ephemeralStorage: randomInt(2, 2048, rand) * 1024 ** 3,
      memory: randomInt(2, 1024, rand) * 1024 ** 2,
      queue: queues[i % queues.length],
      annotations: createAnnotations(annotationKeys, annotationValues, rand),
      jobId: jobId,
      jobSet: jobSets[i % jobSets.length],
      state: state ? state : randomProperty(JobState, rand),
      lastTransitionTime: randomDate(BASE_TIME_MS + 20 * MS_PER_MINUTE, BASE_TIME_MS + 7 * MS_PER_DAY, rand),
      priorityClass: rand() > 0.5 ? "armada-preemptible" : "armada-default",
      node: lastRun?.node,
      cluster: lastRun?.cluster,
      pool: lastRun?.pool,
      exitCode: lastRun?.exitCode,
      runtimeSeconds: lastRunRuntimeSeconds,
    })
  }

  return jobs
}

// CPU requests in thousandths of a CPU (the unit formatCpu expects), using sizes people actually request (250m, 500m, 1, 2, 4, ...)
const CPU_SIZES = [250, 500, 1000, 2000, 4000, 8000, 16000, 32000]

const POOLS = ["default", "cpu", "gpu", "cpu-large"]

const FAILURE_SUBCATEGORIES: Record<string, string[]> = {
  application: ["oom-killed", "non-zero-exit", "timeout"],
  infrastructure: ["node-failure", "network-error", "disk-pressure"],
  scheduling: ["image-pull-backoff", "unschedulable"],
}

const NOT_STARTED_RUN_STATES = [JobRunState.RunPending, JobRunState.RunLeased]

function randomElement<T>(items: T[], rand: () => number): T {
  return items[randomInt(0, items.length, rand)]
}

function createJobRuns(
  n: number,
  jobId: string,
  rand: () => number,
  uuid: () => string,
  ulid: () => string,
): { runs: JobRun[]; lastRunRuntimeSeconds: number | undefined } {
  const runs: JobRun[] = []
  let lastRunRuntimeSeconds: number | undefined = undefined
  // Runs happen one after another, each starting shortly after the previous one ended
  let startedMs = BASE_TIME_MS + 15 * MS_PER_MINUTE + 14 * MS_PER_SECOND
  for (let i = 0; i < n; i++) {
    const runState = randomProperty(JobRunState, rand)
    const hasStarted = !NOT_STARTED_RUN_STATES.includes(runState)
    const isFinished = hasStarted && runState !== JobRunState.RunRunning
    const runtimeSeconds = randomInt(30, 3600, rand)
    const finishedMs = startedMs + runtimeSeconds * MS_PER_SECOND

    let exitCode = undefined
    let failureCategory = undefined
    let failureSubcategory = undefined
    if (isFinished) {
      exitCode = runState === JobRunState.RunSucceeded ? 0 : randomInt(1, 64, rand)
    }
    if (runState === JobRunState.RunFailed) {
      failureCategory = randomElement(Object.keys(FAILURE_SUBCATEGORIES), rand)
      failureSubcategory = randomElement(FAILURE_SUBCATEGORIES[failureCategory], rand)
    }
    const runId = ulid()
    let podName = undefined
    if (runState !== JobRunState.RunPending && runState !== JobRunState.RunLeased) {
      podName = `armada-${runId}`
    }
    runs.push({
      runId: runId,
      cluster: uuid(),
      exitCode,
      jobId: jobId,
      jobRunState: runState,
      node: hasStarted ? uuid() : undefined,
      podName,
      started: new Date(startedMs).toISOString(),
      leased: new Date(startedMs - 10 * MS_PER_SECOND).toISOString(),
      pending: new Date(startedMs - 5 * MS_PER_SECOND).toISOString(),
      finished: isFinished ? new Date(finishedMs).toISOString() : undefined,
      pool: randomElement(POOLS, rand),
      failureCategory,
      failureSubcategory,
    })

    lastRunRuntimeSeconds = hasStarted ? runtimeSeconds : undefined
    startedMs = finishedMs + 27 * MS_PER_SECOND
  }
  return { runs, lastRunRuntimeSeconds }
}

function randomProperty<T>(obj: Record<string, T>, rand: () => number): T {
  const keys = Object.keys(obj)
  return obj[keys[(keys.length * rand()) << 0]]
}

function createAnnotations(
  annotationKeys: string[],
  annotationValues: string[],
  rand: () => number,
): Record<string, string> {
  const annotations: Record<string, string> = {}
  for (const key of annotationKeys) {
    annotations[key] = annotationValues[randomInt(0, annotationValues.length, rand)]
  }
  return annotations
}

export function mergeFilters(filters: JobFilter[]): (job: Job) => boolean {
  return filters.map(filterFn).reduce(
    (aggregatedFn, newFn) => (job) => aggregatedFn(job) && newFn(job),
    () => true,
  )
}

export function filterFn(filter: JobFilter): (job: Job) => boolean {
  return (job) => {
    if (filter.field === "runId" && !filter.isAnnotation) {
      // Run ID filters match if any of the job's runs match
      const matcher = getMatch(filter.match)
      return job.runs.some((run) => matcher(run.runId, filter.value))
    }

    const objectToFilter = filter.isAnnotation ? job.annotations : job

    if (!Object.prototype.hasOwnProperty.call(objectToFilter, filter.field)) {
      if (filter.isAnnotation === undefined || !filter.isAnnotation) {
        // eslint-disable-next-line no-console
        console.error(`Unknown filter field provided: ${JSON.stringify(filter)}`)
      }
      return false
    }
    const matcher = getMatch(filter.match)
    return matcher(objectToFilter[filter.field as JobKey], filter.value)
  }
}

export function getMatch(match: Match): (a: any, b: any) => boolean {
  switch (match) {
    case "exact":
      return (a, b) => a === b
    case "startsWith":
      return (a, b) => isString(a) && isString(b) && a.startsWith(b)
    case "contains":
      return (a, b) => isString(a) && isString(b) && a.includes(b)
    case "greaterThan":
      return (a, b) => a > b
    case "lessThan":
      return (a, b) => a < b
    case "greaterThanOrEqualTo":
      return (a, b) => a >= b
    case "lessThanOrEqualTo":
      return (a, b) => a <= b
    case "anyOf":
      return (a, b) => b.includes(a)
    default:
      // eslint-disable-next-line no-console
      console.error(`Unknown match: ${match}`)
      return () => false
  }
}

export function compareValues(valueA: any, valueB: any, direction: SortDirection): number {
  let val = 0
  if (valueA < valueB) {
    val = -1
  } else if (valueA > valueB) {
    val = 1
  } else if (valueA === valueB) {
    val = 0
  }
  if (direction === "DESC") {
    val = -val
  }
  return val
}

function randomDate(startMs: number, endMs: number, rand: () => number): string {
  return new Date(startMs + rand() * (endMs - startMs)).toISOString()
}

type Resources = {
  cpu: number
  memory: number
  ephemeralStorage: number
  gpu: number
}

export function makeTestJob(
  queue: string,
  jobSet: string,
  jobId: string,
  state: JobState,
  resources?: Resources,
  runs?: JobRun[],
): Job {
  return {
    queue: queue,
    jobSet: jobSet,
    jobId: jobId,
    owner: queue,
    namespace: queue,
    priority: 10,
    cpu: resources?.cpu ?? 1,
    memory: resources?.memory ?? 1024,
    ephemeralStorage: resources?.ephemeralStorage ?? 1024,
    gpu: resources?.gpu ?? 1,
    submitted: new Date().toISOString(),
    lastTransitionTime: new Date().toISOString(),
    state: state,
    runs: runs ?? [],
    annotations: {},
    priorityClass: "armada-preemptible",
  }
}

export function makeManyTestJobs(numJobs: number, numFinishedJobs: number): Job[] {
  const jobs = []
  for (let i = 0; i < numJobs; i++) {
    let state = JobState.Queued
    if (i < numFinishedJobs) {
      state = JobState.Succeeded
    }
    jobs.push(makeTestJob(`queue-0`, `job-set-${i}`, `job-id-${i}`, state))
  }
  return jobs
}

export function makeFakeJobSpec(jobId: string): Record<string, any> {
  return {
    id: jobId,
    // eslint-disable-next-line @cspell/spellchecker
    clientId: "01gvgjbr0jrzvschp2f8jhk6n5",
    jobSetId: "demo-project-0",
    queue: "demo",
    namespace: "default",
    owner: "anonymous",
    podSpec: {
      containers: [
        {
          name: "cpu-burner",
          image: "alpine-stress:latest",
          command: ["sh"],
          args: ["-c", "echo STARTED && sleep 60"],
          resources: {
            limits: { cpu: "200m", "ephemeral-storage": "8Gi", memory: "128Mi", "nvidia.com/gpu": "8" },
            requests: { cpu: "200m", "ephemeral-storage": "8Gi", memory: "128Mi", "nvidia.com/gpu": "8" },
          },
          imagePullPolicy: "IfNotPresent",
        },
      ],
      restartPolicy: "Never",
      terminationGracePeriodSeconds: 1,
      priorityClassName: "armada-default",
    },
    created: "2023-03-14T17:23:21.29874Z",
  }
}

export function getActiveJobSets(jobs: Job[]): Record<string, string[]> {
  const result: Record<string, string[]> = {}
  for (const job of jobs) {
    if ([JobState.Queued, JobState.Leased, JobState.Pending, JobState.Running].includes(job.state)) {
      if (!(job.queue in result)) {
        result[job.queue] = []
      }
      result[job.queue].push(job.jobSet)
    }
  }
  return result
}

const ACTIVE_JOB_STATES = new Set([JobState.Queued, JobState.Leased, JobState.Pending, JobState.Running])

export function filterFakeJobs(jobs: Job[], filters: JobFilter[], activeJobSets: boolean): Job[] {
  const filtered = jobs.filter(mergeFilters(filters))
  return activeJobSets ? filtered.filter((j) => ACTIVE_JOB_STATES.has(j.state)) : filtered
}

let fakeJobsCache: Job[] | undefined

// Shared by every fake-data hook so that the jobs table and group counts are derived from the same dataset
export function getFakeJobs(): Job[] {
  if (fakeJobsCache === undefined) {
    fakeJobsCache = makeRandomJobs(10_000, 42)
  }
  return fakeJobsCache
}
